import React from 'react';
import {
  ComboboxOption,
  createStyles,
  makeStyles,
  TextField,
  Typography,
} from '@bb-ui/react-library';
import { Switch } from '@bb-ui/react-library/dist/components/Switch';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Form from '@rjsf/mui';
import { IJobProvider } from '@lct-jobs/core';
import { IChangeEvent } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { RJSFValidationError } from '@rjsf/utils';
import { ReportingTenantWidget } from './formWidgets/DataSharingReportingTenant';
import { DataSharingPickerWidget } from './formWidgets/DataSharingLinkedTenants';
import { FormBanners } from './FormBanners';

type ProviderComboboxOption = ComboboxOption & {
  provider?: IJobProvider;
};

export interface JobFormProps {
  formSchema: any;
  formData: any;
  onFormChange: (data: { formData: any }) => void;
  onFormSubmit: (s: IChangeEvent<any> & { scheduledStartTime?: string }) => void;
  selectedProvider?: ProviderComboboxOption;
  className?: string;
  lockedFields?: Set<string>;
}

export const useStyles = makeStyles((theme) =>
  createStyles({
    form: {
      '& div[role="radiogroup"] label span:first-child': {
        width: '32px',
        height: '32px',
        borderRadius: '16px',
        '&:hover': {
          backgroundColor: theme.palette.background.b4,
        },
      },
    },
  }),
);

export const JobForm: React.FC<JobFormProps> = ({
  formSchema,
  formData,
  onFormChange,
  onFormSubmit,
  selectedProvider,
  className,
  lockedFields,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [scheduledDateTime, setScheduledDateTime] = React.useState('');

  const handleScheduleEnabledChange = (enabled: boolean) => {
    setScheduleEnabled(enabled);
    if (!enabled) {
      setScheduledDateTime('');
    }
  };

  // Live validation only surfaces errors for fields the user has edited; a submit attempt
  // reveals all of them. A ref is used so transformErrors sees it during rjsf's submit validation.
  const [touchedFields, setTouchedFields] = React.useState<Set<string>>(new Set());
  const submitAttempted = React.useRef(false);

  React.useEffect(() => {
    setTouchedFields(new Set());
    submitAttempted.current = false;
  }, [selectedProvider?.provider?.providerId]);

  const handleFormChange = (data: IChangeEvent) => {
    const changed = Object.keys(data.formData ?? {}).filter(
      (key) => data.formData[key] !== formData?.[key],
    );
    if (changed.some((key) => !touchedFields.has(key))) {
      setTouchedFields((prev) => new Set([...Array.from(prev), ...changed]));
    }
    onFormChange({ formData: data.formData });
  };

  // A field's uiSchema may declare `ui:errorMessages` keyed by validation keyword
  // (e.g. { pattern: 'Must start with a letter' }) to replace the default AJV message.
  const transformErrors = (errors: RJSFValidationError[], uiSchema?: Record<string, any>) =>
    errors
      .filter(
        (error) =>
          submitAttempted.current || touchedFields.has(error.property?.split('.')[1] ?? ''),
      )
      .map((error) => {
        const field = error.property?.split('.')[1] ?? '';
        const message = error.name && uiSchema?.[field]?.['ui:errorMessages']?.[error.name];
        return message ? { ...error, message, stack: `${error.property} ${message}` } : error;
      });

  const handleFormSubmit = (s: IChangeEvent<any>) => {
    const scheduledStartTime =
      scheduleEnabled && scheduledDateTime ? new Date(scheduledDateTime).toISOString() : undefined;

    const submitData = {
      ...s,
      scheduledStartTime,
    };
    onFormSubmit(submitData);
  };

  const mergedUiSchema = React.useMemo(() => {
    const providerUiSchema: Record<string, any> = selectedProvider?.provider?.inputUiSchema ?? {};
    if (!lockedFields || lockedFields.size === 0) return providerUiSchema;
    const disabledOverrides: Record<string, any> = {};
    Array.from(lockedFields).forEach((field) => {
      disabledOverrides[field] = { ...providerUiSchema[field], 'ui:disabled': true };
    });
    return { ...providerUiSchema, ...disabledOverrides };
  }, [selectedProvider?.provider?.inputUiSchema, lockedFields]);

  return formSchema && selectedProvider ? (
    <>
      <FormBanners uiSchema={mergedUiSchema} />
      <div style={{ marginTop: '1rem' }}>
        <FormControlLabel
          control={
            <Switch
              id="schedule-toggle"
              checked={scheduleEnabled}
              onChange={() => handleScheduleEnabledChange(!scheduleEnabled)}
            />
          }
          label={t('jobService.scheduleForLater')}
        />
        {scheduleEnabled && (
          <TextField
            id="scheduled-start-time"
            label={t('jobService.scheduledStartTime')}
            type="datetime-local"
            value={scheduledDateTime}
            onChange={(e: any) => setScheduledDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            style={{ marginTop: '0.5rem', marginBottom: '1rem' }}
          />
        )}
      </div>
      <div
        onSubmitCapture={() => {
          submitAttempted.current = true;
        }}
      >
        <Form
          schema={formSchema}
          formData={formData}
          validator={validator}
          experimental_defaultFormStateBehavior={{ constAsDefaults: 'always' }}
          liveValidate
          showErrorList={false}
          transformErrors={transformErrors}
          focusOnFirstError
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          uiSchema={mergedUiSchema}
          className={className || classes.form}
          widgets={{
            dataSharingLinkedTenants: DataSharingPickerWidget,
            dataSharingReportingTenant: ReportingTenantWidget,
          }}
        />
      </div>
    </>
  ) : (
    <Typography variant="body2" color="error">
      Form schema or provider not available
    </Typography>
  );
};
